import React, { useEffect, useState } from "react";
import { fetchMyReferral } from "@/lib/api";
import { Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Dashboard card that lets the user share their affiliate link and see
 * how close they are to earning the next free month (3 paid conversions
 * = 1 free month). Renders quietly if the API errors.
 */
export default function ShareUmbraCard() {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setStats(await fetchMyReferral());
      } catch (e) {
        console.error("referral fetch failed", e);
      }
    })();
  }, []);

  if (!stats) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(stats.link);
      setCopied(true);
      toast.success("Link copied. Share it anywhere.");
      setTimeout(() => setCopied(false), 2400);
    } catch {
      toast.error("Could not copy — long-press the link instead.");
    }
  };

  const progressPct = Math.min(100, ((3 - stats.next_reward_in) / 3) * 100);

  return (
    <div
      data-testid="share-umbra-card"
      className="border border-obsidian-600 bg-obsidian-800/50 p-6 md:p-8 mb-12 animate-fade-up"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 border border-terracotta text-terracotta flex items-center justify-center shrink-0">
          <Share2 className="h-4 w-4" />
        </div>
        <div>
          <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-1">
            Share Umbra
          </p>
          <p className="font-sans text-[0.7rem] uppercase tracking-[0.2em] text-bone-500">
            Three paid subscribers = one free month for you
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6" data-testid="referral-link-row">
        <input
          readOnly
          value={stats.link}
          data-testid="referral-link-input"
          className="flex-1 min-w-0 bg-obsidian-900 border border-obsidian-600 text-bone-200 font-sans text-sm px-4 py-3"
          onFocus={(e) => e.target.select()}
        />
        <button
          onClick={copy}
          data-testid="referral-copy-btn"
          className="inline-flex items-center justify-center gap-2 border border-terracotta text-terracotta hover:bg-terracotta hover:text-obsidian-900 px-4 py-3 font-sans text-xs uppercase tracking-[0.2em] transition-colors whitespace-nowrap"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>

      <div className="mb-2">
        <div className="flex items-center justify-between mb-2">
          <span className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500">
            {stats.paid_conversions % 3} of 3 toward your next free month
          </span>
          <span className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-terracotta">
            {stats.months_available > 0
              ? `${stats.months_available} month${stats.months_available > 1 ? "s" : ""} unlocked`
              : `${stats.paid_conversions} paid so far`}
          </span>
        </div>
        <div className="h-2 bg-obsidian-800 relative">
          <div
            className="h-full bg-terracotta transition-all"
            style={{ width: `${progressPct}%` }}
            data-testid="referral-progress-bar"
          />
        </div>
      </div>
    </div>
  );
}
